// Record Payment Wizard — the 5-step replacement for PaymentEntryModal
// (Confirm loan → Payment type → Applies to → Amount & date → Review &
// record). Built for the same B3 monthly batch, but each step explains the
// consequence of the choice before it's made: the owner-ruled type lineup
// (Voluntary prepayment is a signpost to Payoff, never an option), required
// acknowledgments on amount mismatches grounded in the pay-ahead engine, and
// an auto-generated idempotency reference so a double-click or retry can
// never record twice. Submits to the UNCHANGED servicing-payments endpoint.
//
// Preserves the SP-2 prefill contract (PaymentEntryPrefill): the cleared
// buyer payment arrives with amount/period preselected and the Stripe-intent
// dedup reference locked — steps still shown, the B3 stays the human gate.

import { useEffect, useMemo, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { Button, Input, Label } from "@/design-system/components/ui";
import { StatusStepper } from "@/design-system/components/ui/StatusStepper";
import { Modal } from "@/components/ui/Modal";
import { calendarDateInputToIso, formatCalendarDate, isoDateInTz, useDateFormatter } from "@/utils/datetime";
import { RecordPaymentExplainModal } from "./RecordPaymentExplainModal";
import type {
  ServicingLedgerPeriodDto,
  ServicingPaymentRecordedDto,
  ServicingPaymentType,
} from "@/api/portfolioAdmin/servicingApi";
import { useRecordServicingPayment } from "@/hooks/portfolioAdmin/useServicing";
import { useTps } from "@/hooks/portfolioAdmin/useTps";
import { useEntityNameMap } from "@/hooks/capitalMarkets/useCounterpartyNames";
import { NO_SERVICER_ASSIGNED, tpsDisplayName } from "@/lib/tpsDisplay";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { fmtMoney } from "./servicingFormat";
import {
  RECORD_PAYMENT_STEPS,
  RECORD_PAYMENT_TYPE_OPTIONS,
  PAYOFF_SIGNPOST,
  DERIVE_FROM_DATE_LABEL,
  NO_SERVICER_CAUTION,
  loanConfirmSentence,
  duplicatePeriodWarning,
  arrearsWarning,
  FUTURE_DATE_ERROR,
  beforeFirstPaymentWarning,
  STALE_DATE_WARNING,
  derivedPeriodMismatchWarning,
  PARTIAL_EXPLANATION,
  PARTIAL_ACK_LABEL,
  payAheadExplanation,
  PAY_AHEAD_ACK_LABEL,
  FAR_ABOVE_WARNING,
  REVIEW_AMOUNT_RECHECK_NOTICE,
  expectedWithFeesBreakdown,
  OUTSTANDING_FEES_EXPLANATION,
  prefillMismatchWarning,
  LOCKED_REFERENCE_NOTE,
  AUTO_REFERENCE_NOTE,
  friendlyServerError,
  BOARDING_PERIOD_OPTION_SUFFIX,
  BOARDING_PERIOD_MONTHLY_BLOCKED,
  BOARDING_PERIOD_RECORD_BLOCKED_ERROR,
  BOARDING_PERIOD_DEFAULT_SKIPPED_HINT,
  BOARDING_FIRST_PAYMENT_REFUSED,
} from "./recordPaymentCopy";

const OPEN_STATUSES = new Set(["upcoming", "missed", "partial", "late"]);
const UNPAID_PAST_STATUSES = new Set(["missed", "partial", "late"]);
const TOLERANCE = 0.004;

/** SP-2 Monthly Cycle Cockpit — prefill facts from a cleared (Stripe/TPS)
 *  buyer payment. The reference is the locked dedup key and is submitted
 *  verbatim; amount/period arrive preselected but stay editable (the B3 is
 *  the human gate — a mismatch warns and asks for acknowledgment, never
 *  blocks). */
export interface PaymentEntryPrefill {
  amount: number;
  periodStart: string;
  externalReference: string;
  /** The bill's amount due — the other anchor the mismatch warning checks. */
  expectedAmount: number;
  payerName?: string | null;
}

/** Pay-ahead preview, computed client-side from the loaded schedule window.
 *  Mirrors the engine's roll-forward: the excess above the anchor period's
 *  remaining expected covers future installments oldest-first. When the
 *  window ends without a balloon row the schedule continues past what we
 *  can see; when it DOES include the balloon, leftover beyond it means the
 *  server will reject and point at the Payoff flow. */
export function computePayAheadPreview(
  periods: ServicingLedgerPeriodDto[],
  anchorIndex: number,
  amount: number,
  /** What the anchor period actually still owes, when that is more than its own
   *  schedule remainder — the bill's amount due in prefill mode, or the schedule
   *  remainder plus outstanding borrower fees in manual mode. Money settling a
   *  fee obligation is NOT paying a future installment, so measuring the excess
   *  above the bare schedule remainder describes the wrong disposition of it.
   *  Passing the SAME value the below/above-expected thresholds compare against
   *  is what stops the warning and the preview disagreeing. Omitted → the
   *  schedule remainder, exactly as before.
   *
   *  Only the ANCHOR is widened: fees are one-time, so future periods keep
   *  costing their own schedule remainder. */
  anchorRemainingOverride?: number,
): {
  excess: number;
  fullyCovered: number;
  lastPartial: boolean;
  beyondWindow: boolean;
  exceedsRemainingSchedule: boolean;
} {
  const anchor = periods.find((p) => p.periodIndex === anchorIndex);
  const none = {
    excess: 0,
    fullyCovered: 0,
    lastPartial: false,
    beyondWindow: false,
    exceedsRemainingSchedule: false,
  };
  if (!anchor) return none;
  const anchorRemaining =
    anchorRemainingOverride ?? anchor.expectedAmount - anchor.receivedAmount;
  let excess = amount - anchorRemaining;
  if (excess <= TOLERANCE) return none;
  const totalExcess = excess;

  const future = periods
    .filter((p) => p.periodIndex > anchorIndex)
    .sort((a, b) => a.periodIndex - b.periodIndex);
  let fullyCovered = 0;
  let lastPartial = false;
  let sawBalloon = anchor.isBalloon;
  for (const p of future) {
    sawBalloon = sawBalloon || p.isBalloon;
    const remaining = Math.max(0, p.expectedAmount - p.receivedAmount);
    if (remaining <= TOLERANCE) continue;
    if (excess >= remaining - TOLERANCE) {
      fullyCovered += 1;
      excess -= remaining;
    } else {
      lastPartial = true;
      excess = 0;
      break;
    }
    if (excess <= TOLERANCE) {
      excess = 0;
      break;
    }
  }
  const leftover = excess > TOLERANCE;
  return {
    excess: totalExcess,
    fullyCovered,
    lastPartial,
    beyondWindow: leftover && !sawBalloon,
    exceedsRemainingSchedule: leftover && sawBalloon,
  };
}

export function RecordPaymentWizard({
  loan,
  periods,
  open,
  onClose,
  prefill,
  nextPayablePeriod,
  boardingFirstPaymentOnFile = false,
  openBorrowerFeeObligations = 0,
  onViewAllocation,
}: {
  loan: LoanSummaryDto;
  periods: ServicingLedgerPeriodDto[];
  open: boolean;
  onClose: () => void;
  prefill?: PaymentEntryPrefill;
  /** The oldest still-owed period across the FULL remaining term (the ledger's
   *  `nextPayablePeriod`) — "next period after last paid". It may sit BEYOND the
   *  bounded `periods` display window when the borrower is paid ahead; the wizard
   *  merges it into the options and defaults to it, so an ordinary payment never
   *  falls back to the calendar-bucket "derive from received date". */
  nextPayablePeriod?: ServicingLedgerPeriodDto | null;
  /** The ledger's `boardingFirstPaymentOnFile` — true when the borrower's
   *  boarding first payment (period 0) is on file and live. That is EXACTLY the
   *  server's own condition for refusing a monthly P&I run against period 0
   *  (ServicingPaymentOrchestrator.FindLiveFirstPaymentForPeriodAsync), so the
   *  wizard uses it to keep its default — and a deliberate selection — off a
   *  period the server will not record. Absent reads as "not blocked". */
  boardingFirstPaymentOnFile?: boolean;
  /** The ledger's `openBorrowerFeeObligations` — borrower-owed fee money that is
   *  live now and sits in NO period's `expectedAmount` (approved pipeline fees,
   *  measured by their WF1 obligations' still-open balance).
   *
   *  A period's `expectedAmount` is the four schedule terms; the borrower's
   *  statement bills `expectedAmount` PLUS these fees. Prefilling the bare
   *  schedule remainder therefore asks for less than the borrower was billed —
   *  and the engine makes the difference land on the most junior WF1 leg, since
   *  it sizes `extraCash = paid − scheduledTotal` off those same four terms and
   *  funds the fee obligations out of that extra first. Paying the schedule
   *  amount exactly leaves no extra, so the fee bites the scheduled cash and
   *  starves seller principal by exactly the fee.
   *
   *  IGNORED in prefill mode: the cleared-payment flow's anchor is the bill's
   *  own amount due, which already includes these fees — adding them again
   *  would double-count. Absent reads as "no fees outstanding", so the whole
   *  existing book is unchanged. */
  openBorrowerFeeObligations?: number;
  /** Parent-owned drill-in (the tab's ?payment= deep link) — the success
   *  panel only offers "View allocation" when the host surface has one. */
  onViewAllocation?: (paymentId: string) => void;
}) {
  const { tz } = useDateFormatter();
  const mutation = useRecordServicingPayment(loan.id);

  // Prefill mode already carries the bill's amount due (statement.AmountDue,
  // which folds these fees in), so the fee widening applies to the MANUAL path
  // only — otherwise the same fee would be counted twice.
  const openFees = prefill ? 0 : Math.max(openBorrowerFeeObligations, 0);
  const hasOpenFees = openFees > TOLERANCE;

  /** What recording a payment against this period should actually collect: the
   *  period's own schedule remainder plus any borrower fee money outstanding.
   *  With no open fees this is byte-identical to the schedule remainder, which
   *  is what it was before — including for an overpaid period, where the
   *  remainder is deliberately left negative rather than clamped. */
  const owedFor = (p: ServicingLedgerPeriodDto | null | undefined) =>
    p ? p.expectedAmount - p.receivedAmount + openFees : null;

  // The selectable periods = the bounded display window PLUS the true next
  // payable period when it lies beyond that window (paid-ahead case), so the
  // wizard can pre-select and submit the real next-owed period instead of the
  // "derive from received date" calendar bucket. Deduped + ordered by index.
  const effectivePeriods = useMemo(() => {
    if (
      !nextPayablePeriod ||
      periods.some((p) => p.periodIndex === nextPayablePeriod.periodIndex)
    ) {
      return periods;
    }
    return [...periods, nextPayablePeriod].sort(
      (a, b) => a.periodIndex - b.periodIndex,
    );
  }, [periods, nextPayablePeriod]);

  const openPeriods = useMemo(
    () =>
      effectivePeriods
        .filter(
          (p) =>
            OPEN_STATUSES.has(p.status) &&
            p.receivedAmount < p.expectedAmount - TOLERANCE,
        )
        .sort((a, b) => a.periodIndex - b.periodIndex),
    [effectivePeriods],
  );
  // Period 0 is the loan's first-payment period, settled at boarding by
  // first-payment recording (100% to Phoenix, outside the waterfall). While that
  // boarding payment is on file and live the server REFUSES a monthly P&I run
  // against it — so it is not a payable period for this wizard's default type,
  // however "still owed" the ledger reports it to be.
  //
  // It genuinely can still be owed: a loan boarded at exactly the scheduled P&I
  // is billed P&I + the Payment Administration Fee, so period 0 reads Partial
  // and IS the ledger's `nextPayablePeriod`. Defaulting there submitted a payment
  // the server always refused, and the wizard reported it as "already recorded"
  // — a success message for a no-op. The shortfall is real; it is simply not
  // collectible through a monthly P&I entry against period 0.
  const isBoardingPeriod = (p: ServicingLedgerPeriodDto) =>
    boardingFirstPaymentOnFile && p.periodIndex === 0;

  // The manual-entry default period: the true next payable period (which may be
  // beyond the display window), else the oldest open period in the window —
  // skipping the boarding period, which no monthly P&I entry can land on. This
  // is the period the amount + submitted periodStart default to.
  const defaultablePeriods = useMemo(
    () =>
      boardingFirstPaymentOnFile
        ? openPeriods.filter((p) => p.periodIndex !== 0)
        : openPeriods,
    [openPeriods, boardingFirstPaymentOnFile],
  );
  const firstOpenPeriod =
    (nextPayablePeriod && !isBoardingPeriod(nextPayablePeriod)
      ? nextPayablePeriod
      : null) ??
    defaultablePeriods[0] ??
    null;
  // Prefill mode — the cleared payment's period, matched onto the ledger's
  // schedule row by due date (falls back to submitting the prefill's
  // periodStart directly when the row isn't in `periods`).
  const prefillPeriod = prefill
    ? (effectivePeriods.find((p) => p.dueDate === prefill.periodStart) ?? null)
    : null;

  const [step, setStep] = useState(0);
  const [paymentType, setPaymentType] = useState<ServicingPaymentType>("monthly_p_and_i");
  // Default the manual entry to the FIRST OPEN period — the same period the
  // amount defaults to (below) — rather than "derive from date". Leaving it on
  // derive let a recently-boarded loan's entry resolve to period 0 (the closing
  // month, or clamped there for a future first-payment date), where the
  // boarding first payment already lives, so the orchestrator's period-0
  // double-record guard swallowed a genuine later payment as "already
  // recorded". Naming the intended period explicitly keeps every server-side
  // guard intact while landing the payment where staff mean it. "Derive from
  // date" stays selectable. Prefill keeps the cleared payment's own period.
  const [periodIndex, setPeriodIndex] = useState<number | "">(
    prefill ? (prefillPeriod?.periodIndex ?? "") : (firstOpenPeriod?.periodIndex ?? ""),
  );
  const [amount, setAmount] = useState<string>(() => {
    if (prefill) return prefill.amount.toFixed(2);
    return firstOpenPeriod ? owedFor(firstOpenPeriod)!.toFixed(2) : "";
  });
  // Has a staff member ENTERED something in the amount field? The re-derive
  // below must never replace a figure someone typed off a remittance advice,
  // and this is the only thing standing between it and doing exactly that.
  //
  // The invariant, within one open: `false` means the field holds a value the
  // WIZARD wrote, `true` means it holds one the USER put there. Entry is caught
  // at the input itself — onChange, plus the paste and drop gestures React's
  // value tracking would otherwise swallow (see the handlers). It is set back
  // to false only inside a block that overwrites the field with a derived
  // value, and on a reopen.
  //
  // That pairing is deliberate rather than incidental: `selectType` and
  // `selectPeriod` both reach `setAmount` CONDITIONALLY, so clearing the flag
  // beside their ack resets would drop it while a typed figure survived. Only
  // the `selectType` half of that is reachable today — a boarding-period loan
  // where `anchor` is null — and it is what the "keeps a typed figure when a
  // type change writes no amount" test pins.
  const [amountTouched, setAmountTouched] = useState(false);
  // Default to TODAY as a Phoenix calendar day — the UTC date-part reads as
  // tomorrow for an evening entry (after 5 PM MST).
  // Seeded ONCE, in the viewer's zone. A lazy initialiser is deliberate: it
  // must not re-seed when `tz` resolves later, because by then the staff member
  // may have typed a date and a re-seed would overwrite it — the same shape as
  // the amount-field defect this wizard already carries pins against.
  const [receivedDate, setReceivedDate] = useState<string>(() => isoDateInTz(new Date(), tz));
  const [notes, setNotes] = useState("");
  const [partialAck, setPartialAck] = useState(false);
  const [payAheadAck, setPayAheadAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ServicingPaymentRecordedDto | null>(null);
  const [askAiOpen, setAskAiOpen] = useState(false);
  // Idempotency (owner-ruled): one reference per wizard INSTANCE, generated
  // at open — a double-click or a retry after an error resubmits the same
  // key and dedupes server-side (wasAlreadyProcessed). Prefill keeps its
  // supplied Stripe-intent reference untouched.
  const [autoReference, setAutoReference] = useState(
    () => `staff-entry:${crypto.randomUUID()}`,
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError(null);
    setResult(null);
    setPartialAck(false);
    setPayAheadAck(false);
    setAutoReference(`staff-entry:${crypto.randomUUID()}`);
    // Belt-and-braces for a persistent mount, where a previous session's typing
    // is not this session's intent. Unreachable today — both consumers mount
    // conditionally and pass `open` always true, which is the same reason the
    // note below gives for not resetting the value here.
    setAmountTouched(false);
    // Selection/amount state deliberately NOT reset here — both consumers
    // conditionally mount the wizard, so the initializers run per open; this
    // effect only re-arms the parts that must never leak across a reopen of
    // a persistent mount.
  }, [open]);

  // --- Step 1 — loan & servicer -------------------------------------------
  const tpsId: string | null = loan.tpsId ?? null;
  const tpsQuery = useTps(tpsId ?? undefined);
  const tps = tpsQuery.data;
  const { map: entityNames } = useEntityNameMap(!!tps?.entityId);
  const tpsName = useMemo(() => {
    if (!tpsId) return NO_SERVICER_ASSIGNED;
    if (tps) return tpsDisplayName(tps, entityNames.get(tps.entityId));
    return "Loading…";
  }, [tpsId, tps, entityNames]);
  const noServicer = tpsName === NO_SERVICER_ASSIGNED;

  // --- Step 2 — type ---------------------------------------------------------
  const selectedType = RECORD_PAYMENT_TYPE_OPTIONS.find((t) => t.value === paymentType)!;
  const isFeeEvent = selectedType.isFeeEvent === true;
  // The mismatch/pay-ahead machinery applies to the scheduled monthly
  // installment types the engine sizes each_period; a balloon IS the final
  // payment (no pay-ahead), and fee events have no schedule anchor.
  const isMonthlyLike =
    paymentType === "monthly_p_and_i" || paymentType === "bmsp_extension_monthly_payment";

  const selectType = (value: ServicingPaymentType) => {
    setPaymentType(value);
    setPartialAck(false);
    setPayAheadAck(false);
    const option = RECORD_PAYMENT_TYPE_OPTIONS.find((t) => t.value === value)!;
    if (prefill) return; // the cleared payment is the amount anchor — never clobber
    if (option.isFeeEvent) {
      setAmount("");
      // Unobservable on its own (a fee event makes the re-derive inert), but the
      // field no longer holds a user value, so the flag must not say it does.
      setAmountTouched(false);
      return;
    }
    const anchor =
      periodIndex === ""
        ? firstOpenPeriod
        : (effectivePeriods.find((p) => p.periodIndex === periodIndex) ?? null);
    // Paired with the write, never hoisted above it: with no anchor there is no
    // write, so a typed figure must keep its claim on the field. This is the
    // reachable half of that pairing — a boarding-period loan leaves `anchor`
    // null while the field still holds what staff typed.
    if (anchor) {
      setAmount(owedFor(anchor)!.toFixed(2));
      setAmountTouched(false);
    }
  };

  // --- Step 3 — period --------------------------------------------------------
  const selectedPeriod =
    periodIndex === "" ? null : (effectivePeriods.find((p) => p.periodIndex === periodIndex) ?? null);

  // Scoped to the ONE type the server actually guards
  // (ServicingPaymentOrchestrator: "Scoped to MonthlyPandI: a period-0 fee event
  // / prepayment is a distinct charge and proceeds"). Staff can still deliberately
  // select the boarding period for any other type; only the refused combination
  // is blocked, so the UI is never stricter than the endpoint in a way that hides
  // a legitimate action.
  const monthlyBlocked = (p: ServicingLedgerPeriodDto | null | undefined) =>
    p != null && paymentType === "monthly_p_and_i" && isBoardingPeriod(p);
  const boardingPeriodBlocked = monthlyBlocked(selectedPeriod);

  const selectPeriod = (idx: number | "") => {
    setPeriodIndex(idx);
    setPartialAck(false);
    setPayAheadAck(false);
    const period = idx === "" ? firstOpenPeriod : (effectivePeriods.find((p) => p.periodIndex === idx) ?? null);
    // In prefill mode the amount anchor is the cleared payment, not the
    // period's remaining expected — never clobber it on a period change.
    if (period && !isFeeEvent && !prefill) {
      setAmount(owedFor(period)!.toFixed(2));
      // Inside the write's own block, for the same reason as selectType above —
      // though no path reaches this guard's skip arm with a typed value today
      // (a null period leaves `anchorPeriod` null too, so the re-derive is inert
      // anyway). Kept so the pairing rule holds uniformly at every write.
      setAmountTouched(false);
    }
  };

  // Presence-based duplicate check — the ledger row carries ONE paymentId /
  // receivedAmount, so this can't count multiple receipts on the same
  // period; it flags that at least one exists.
  const duplicateWarning =
    selectedPeriod && (selectedPeriod.paymentId != null || selectedPeriod.receivedAmount > 0)
      ? duplicatePeriodWarning(
          fmtMoney(selectedPeriod.receivedAmount),
          selectedPeriod.receivedDate ? formatCalendarDate(selectedPeriod.receivedDate) : "file",
        )
      : null;

  const oldestUnpaidBeforeSelection = useMemo(() => {
    if (!selectedPeriod) return null;
    return (
      effectivePeriods
        .filter(
          (p) =>
            p.periodIndex < selectedPeriod.periodIndex &&
            UNPAID_PAST_STATUSES.has(p.status) &&
            p.receivedAmount < p.expectedAmount - TOLERANCE,
        )
        .sort((a, b) => a.periodIndex - b.periodIndex)[0] ?? null
    );
  }, [effectivePeriods, selectedPeriod]);

  // --- Step 4 — amount & date ---------------------------------------------------
  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;

  // The comparison anchor — ONE number, shared by the below/above-expected
  // acknowledgments, the "Expected:" caption and the pay-ahead preview: the bill
  // in prefill mode (statement.AmountDue, fees already folded in), else the
  // selected (or oldest open) period's remaining expected PLUS any borrower fee
  // money outstanding. Both branches are therefore "what the borrower owes",
  // never "what the amortization schedule alone bills".
  const anchorPeriod = selectedPeriod ?? prefillPeriod ?? firstOpenPeriod;
  const expectedComparable = prefill
    ? prefill.expectedAmount
    : owedFor(anchorPeriod);

  /** What an UNTOUCHED amount field should be showing, as the exact string the
   *  wizard would write. `null` = leave the field exactly as it is.
   *
   *  In manual mode this IS `expectedComparable` above — the same number the
   *  "Expected:" caption renders and both acknowledgment thresholds compare
   *  against — so a re-derive can never leave the amount measured against a
   *  different anchor than the one PR #1099 single-sourced. It is written as
   *  `owedFor(anchorPeriod)` rather than by reading `expectedComparable`
   *  because that expression's OTHER branch is `prefill.expectedAmount`: a
   *  later edit that dropped the prefill guard below as redundant would then
   *  record the BILL in place of the cleared payment, against a locked Stripe
   *  dedup key, and suppress the very `prefillMismatch` check that exists to
   *  surface the difference.
   *
   *  Null in prefill mode (the cleared payment is the anchor, and it
   *  legitimately differs from the bill), for a fee event (no schedule
   *  anchor — the deliberate blank must survive), and with no anchor period.
   *
   *  A STRING, and scalar on purpose. TanStack's structural sharing PRESERVES
   *  `periods` and its element identities across a refetch that moves only the
   *  top-level `openBorrowerFeeObligations` — the fee approve/waive case this
   *  exists for — so an effect keyed on `anchorPeriod` or `periods` identity
   *  would never fire on the one transition that matters, while a fixture that
   *  rebuilt its periods would hand it fresh objects and pass anyway. */
  const untouchedAmount =
    prefill || isFeeEvent ? null : (owedFor(anchorPeriod)?.toFixed(2) ?? null);

  // Re-derive the amount when the figure it was derived FROM moves underneath
  // an untouched field. The initializer runs once at mount, but the caption and
  // the thresholds are computed during render — so a ledger refetch landing a
  // beat after the wizard opened (waive a fee, then immediately record the
  // payment; or simply return to this tab with the query stale) left the amount
  // on the pre-waive figure while everything around it moved, and the wizard
  // demanded a pay-ahead acknowledgment for its own default. Only the amount
  // was ever stale; the data never was.
  useEffect(() => {
    if (!open || amountTouched || untouchedAmount == null) return;
    setAmount(untouchedAmount);
    // An acknowledgment attests to a SPECIFIC figure, so every writer of
    // `amount` voids them. Deliberately AFTER the guard, never above it: above,
    // this un-ticks a deliberate acknowledgment and disables Next mid-flow with
    // no explanation. Both live cases are already covered by a guard clause
    // rather than by these lines — a ticked ack in MANUAL mode implies the field
    // was entered, and in PREFILL mode an ack ticks routinely with nothing
    // entered (the cleared payment legitimately differs from the bill) but
    // `untouchedAmount` is null there.
    setPartialAck(false);
    setPayAheadAck(false);
  }, [open, amountTouched, untouchedAmount]);

  const belowExpected =
    isMonthlyLike &&
    amountValid &&
    expectedComparable != null &&
    parsedAmount < expectedComparable - TOLERANCE;
  const aboveExpected =
    isMonthlyLike &&
    amountValid &&
    expectedComparable != null &&
    parsedAmount > expectedComparable + TOLERANCE;

  const payAhead = useMemo(() => {
    if (!aboveExpected || !anchorPeriod) return null;
    // Measured above the SAME anchor the acknowledgment thresholds use, so the
    // preview can never describe fee money as paying a future installment.
    return computePayAheadPreview(
      effectivePeriods,
      anchorPeriod.periodIndex,
      parsedAmount,
      expectedComparable ?? undefined,
    );
  }, [aboveExpected, anchorPeriod, effectivePeriods, parsedAmount, expectedComparable]);

  const payAheadSentence = useMemo(() => {
    if (!aboveExpected) return null;
    if (!anchorPeriod || !payAhead) {
      return "This amount is above the expected payment — the excess pays future installments early.";
    }
    // A partial tail is itself a paid-ahead period — count it so "the last"
    // refers to a period included in the count.
    const n = payAhead.fullyCovered + (payAhead.lastPartial ? 1 : 0);
    const parts = [
      `This amount covers ${formatCalendarDate(anchorPeriod.dueDate)} plus pays ahead ${n} more period${n === 1 ? "" : "s"}`,
    ];
    if (payAhead.lastPartial) parts.push(", the last partially");
    if (payAhead.beyondWindow) parts.push(", and continues into later periods");
    return `${parts.join("")}.`;
  }, [aboveExpected, anchorPeriod, payAhead]);

  const farAbove = payAhead?.exceedsRemainingSchedule === true;

  // Non-blocking dual-anchor check for the SP-2 prefill flow: warn when the
  // entered amount matches NEITHER the bill nor the cleared payment.
  const prefillMismatch =
    prefill != null &&
    amountValid &&
    Math.abs(parsedAmount - prefill.amount) > TOLERANCE &&
    Math.abs(parsedAmount - prefill.expectedAmount) > TOLERANCE;

  // Today in the VIEWER's zone, not Phoenix. This gates `dateIsFuture` →
  // `dateValid` → the wizard's advance, and it is the ONLY bound: neither
  // `ServicingPaymentControllers` nor `ServicingPaymentOrchestrator.ProcessAsync`
  // rejects a future received date (`PayoffOrchestrator` does, on a different
  // path). So a Phoenix-pinned "today" REFUSES VALID INPUT rather than
  // mislabelling it — after 17:00 Phoenix a viewer east of it is already on
  // tomorrow and cannot record a payment received today. Widening this cannot
  // let through something a server bound would refuse, because there is none.
  const todayStr = isoDateInTz(new Date(), tz);
  const dateIsFuture = receivedDate.length > 0 && receivedDate > todayStr;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(receivedDate) && !dateIsFuture;

  const twoMonthsAgo = useMemo(() => {
    const d = new Date();
    // NOTE: `setMonth` overflows rather than clamping — two months before
    // Apr 30 lands on Mar 2, not Feb 28. That is a separate finding about
    // calendar month arithmetic and is deliberately NOT fixed here; this is a
    // soft staleness WARNING, not a bound, so the day is immaterial to it.
    d.setMonth(d.getMonth() - 2);
    return isoDateInTz(d, tz);
  }, [tz]);
  const staleDate = dateValid && receivedDate < twoMonthsAgo;
  const firstPaymentDay = loan.firstPaymentDate ? loan.firstPaymentDate.slice(0, 10) : null;
  const beforeFirstPayment =
    dateValid && firstPaymentDay != null && receivedDate < firstPaymentDay;

  // Client heuristic for the server's derive-from-date: the schedule is
  // monthly, so the received date's calendar month names the period it would
  // land on. Only warn when an EXPLICIT selection disagrees.
  const derivedPeriod = useMemo(() => {
    if (!dateValid) return null;
    const month = receivedDate.slice(0, 7);
    return effectivePeriods.find((p) => p.dueDate.slice(0, 7) === month) ?? null;
  }, [dateValid, receivedDate, effectivePeriods]);
  const derivedMismatch =
    selectedPeriod != null &&
    derivedPeriod != null &&
    derivedPeriod.periodIndex !== selectedPeriod.periodIndex;

  const amountStepValid =
    amountValid &&
    dateValid &&
    (!belowExpected || partialAck) &&
    (!aboveExpected || payAheadAck);

  // --- Navigation ----------------------------------------------------------------
  const isLast = step === RECORD_PAYMENT_STEPS.length - 1;
  const nextDisabled =
    // Checked at the LAST step as well as the amount step. `amountStepValid` is
    // computed during render and was never stale — the gate simply did not read
    // it here, so a mismatch arising AFTER the acknowledgment step (a ledger
    // refetch landing under an open wizard, a bill regenerating beneath a
    // cleared payment) left Record enabled over an acknowledgment nobody gave.
    ((step === 3 || isLast) && !amountStepValid) ||
    (isLast && mutation.isPending) ||
    // Block at the period step and at the final record: the server refuses this
    // combination outright, so advancing can only end in a no-op reported as
    // success. Not an acknowledgment checkbox like the mismatch guards — there is
    // nothing to acknowledge, the action simply cannot be performed.
    ((step === 2 || isLast) && boardingPeriodBlocked);

  const submit = async () => {
    setError(null);
    if (!amountValid) {
      setError("Enter a positive amount.");
      return;
    }
    // Belt-and-braces behind the disabled Record button: the server refuses this
    // combination, so submitting could only produce a no-op reported as success.
    if (boardingPeriodBlocked) {
      setError(BOARDING_PERIOD_RECORD_BLOCKED_ERROR);
      return;
    }
    // Noon-UTC anchor (calendarDateInputToIso) so the entered calendar day
    // survives rendering in any timezone — a T00:00:00Z stamp reads as the
    // previous day once converted to Phoenix time.
    const receivedIso = calendarDateInputToIso(receivedDate);
    if (!receivedIso) {
      setError("Enter a valid received date.");
      return;
    }
    try {
      const recorded = await mutation.mutateAsync({
        paymentType,
        amount: parsedAmount,
        receivedDate: receivedIso,
        periodStart: selectedPeriod?.dueDate ?? prefill?.periodStart ?? null,
        // Prefill mode submits the locked dedup reference verbatim; the
        // manual path sends the per-instance auto-generated key.
        externalReference: prefill ? prefill.externalReference : autoReference,
        notes: notes.trim() || null,
      });
      setResult(recorded);
    } catch (err) {
      setError(
        friendlyServerError(
          err instanceof Error ? err.message : "The payment could not be recorded.",
        ),
      );
    }
  };

  const handleNext = () => {
    if (isLast) {
      void submit();
      return;
    }
    setStep((s) => Math.min(s + 1, RECORD_PAYMENT_STEPS.length - 1));
  };
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const periodLabel = selectedPeriod
    ? `${formatCalendarDate(selectedPeriod.dueDate)} — expected ${fmtMoney(selectedPeriod.expectedAmount)}`
    : prefill && !prefillPeriod
      ? formatCalendarDate(prefill.periodStart)
      : DERIVE_FROM_DATE_LABEL;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      size="lg"
      title="Record payment"
      description="Records the payment and runs the WF1 allocation — every line lands in the drill-in. The run is then held for B3 review before its payout is released."
      footer={
        result ? (
          <>
            {onViewAllocation && (
              <Button
                variant="secondary"
                onClick={() => {
                  onViewAllocation(result.paymentId);
                  onClose();
                }}
                data-testid="view-allocation"
              >
                View allocation
              </Button>
            )}
            <Button onClick={onClose} data-testid="wizard-done">
              Done
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={mutation.isPending}
              data-testid="wizard-cancel"
            >
              Cancel
            </Button>
            {step > 0 && (
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={mutation.isPending}
                data-testid="wizard-back"
              >
                Back
              </Button>
            )}
            <Button
              loading={mutation.isPending}
              disabled={nextDisabled}
              onClick={handleNext}
              data-testid="wizard-next"
            >
              {isLast ? "Record payment" : "Next"}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="record-payment-wizard">
        {!result && (
          <div className="flex items-start justify-between gap-space-3">
            <StatusStepper
              steps={RECORD_PAYMENT_STEPS.map((s) => ({ key: s.key, label: s.label }))}
              currentIndex={step}
              label="Record payment steps"
              data-testid="record-payment-stepper"
            />
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setAskAiOpen(true)}
              data-testid="ask-ai-button"
            >
              <MessageCircleQuestion size={14} aria-hidden /> Ask AI
            </Button>
          </div>
        )}

        {result ? (
          <div className="flex flex-col gap-space-3" data-testid="record-success">
            <p className="text-small text-onyx m-0">
              {/* Two very different outcomes reach wasAlreadyProcessed: ordinary
                  request idempotency (the caller's payment WAS recorded, just
                  earlier) and the period-0 boarding guard (nothing was recorded
                  and nothing can be). Only the server can tell them apart. */}
              {result.wasAlreadyProcessed
                ? result.matchedBoardingFirstPayment
                  ? BOARDING_FIRST_PAYMENT_REFUSED
                  : "This payment was already recorded — nothing was duplicated."
                : result.pendingReview
                  ? `Payment recorded — ${fmtMoney(result.totalDistributed)} allocated. It's now held for B3 review before payout is released.`
                  : `Payment recorded — ${fmtMoney(result.totalDistributed)} distributed through the waterfall.`}
            </p>
            <p className="text-caption text-gray-mid m-0">
              {result.wasAlreadyProcessed && result.matchedBoardingFirstPayment
                ? "Go back and choose the next open period to record this payment."
                : result.pendingReview
                  ? "It appears in Payment Reviews for B3 to approve. Open the allocation drill-in to see every line."
                  : "The schedule and loan health refresh automatically. Open the allocation drill-in to see every line."}
            </p>
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="flex flex-col gap-space-3" data-testid="wizard-loan-summary">
                <p className="text-small text-onyx m-0">
                  {loanConfirmSentence(loan.loanNumber, tpsName)}
                </p>
                <dl className="grid grid-cols-[10rem_1fr] gap-y-space-2 text-small text-onyx m-0 p-space-3 border border-border-light rounded-md bg-white-warm">
                  <dt className="text-gray-mid">Loan</dt>
                  <dd className="m-0 font-semibold">{loan.loanNumber}</dd>
                  <dt className="text-gray-mid">Borrower</dt>
                  <dd className="m-0">{loan.borrowerName ?? "—"}</dd>
                  <dt className="text-gray-mid">Current balance</dt>
                  <dd className="m-0">{fmtMoney(loan.currentBalance)}</dd>
                  <dt className="text-gray-mid">Servicer (TPS)</dt>
                  <dd className="m-0" data-testid="wizard-tps-name">
                    {tpsName}
                  </dd>
                </dl>
                {noServicer && (
                  <p
                    className="text-small text-status-warning border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2 m-0"
                    role="status"
                    data-testid="wizard-tps-caution"
                  >
                    {NO_SERVICER_CAUTION}
                  </p>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="flex flex-col gap-space-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
                  {RECORD_PAYMENT_TYPE_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => selectType(t.value)}
                      aria-pressed={paymentType === t.value}
                      className={
                        "text-left p-space-3 rounded-md border transition-colors duration-fast " +
                        (paymentType === t.value
                          ? "border-gold bg-gold/10"
                          : "border-border-light bg-white-pure hover:border-gold")
                      }
                      data-testid={`type-option-${t.value}`}
                    >
                      <div className="font-display text-label font-semibold uppercase tracking-label text-onyx">
                        {t.label}
                      </div>
                      <div className="text-caption text-gray-mid mt-space-1">{t.blurb}</div>
                    </button>
                  ))}
                </div>
                <div className="p-space-3 border border-border-light rounded-md bg-white-warm">
                  <p className="text-small text-onyx m-0">{selectedType.facts}</p>
                  {selectedType.warning && (
                    <p
                      className="text-small text-status-warning m-0 mt-space-2"
                      role="status"
                      data-testid="type-warning"
                    >
                      {selectedType.warning}
                    </p>
                  )}
                </div>
                <div
                  className="p-space-3 border border-border-light rounded-md bg-white-warm"
                  data-testid="payoff-signpost"
                >
                  <p className="text-small font-semibold text-onyx m-0">
                    {PAYOFF_SIGNPOST.title}
                  </p>
                  <p className="text-small text-gray-mid m-0 mt-space-1">
                    {PAYOFF_SIGNPOST.body}
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-space-3">
                <div>
                  <Label htmlFor="entry-period">Applies to period</Label>
                  <select
                    id="entry-period"
                    className="w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
                    value={periodIndex}
                    onChange={(e) =>
                      selectPeriod(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    data-testid="entry-period"
                  >
                    <option value="">{DERIVE_FROM_DATE_LABEL}</option>
                    {effectivePeriods.map((p) => (
                      <option key={p.periodIndex} value={p.periodIndex}>
                        {formatCalendarDate(p.dueDate)} — expected {fmtMoney(p.expectedAmount)}
                        {/* Marked, not hidden: staff may still have a legitimate
                            reason to select it with another payment type. */}
                        {isBoardingPeriod(p) ? BOARDING_PERIOD_OPTION_SUFFIX : ""}
                        {nextPayablePeriod?.periodIndex === p.periodIndex &&
                        !isBoardingPeriod(p)
                          ? " — next payment due"
                          : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-caption text-gray-mid m-0 mt-space-1">
                    {nextPayablePeriod && isBoardingPeriod(nextPayablePeriod)
                      ? BOARDING_PERIOD_DEFAULT_SKIPPED_HINT
                      : nextPayablePeriod
                        ? "Defaults to the next payment due — the oldest period still owed, even if it’s further out than the schedule below shows. Change it only to apply this payment to a different period."
                        : `Leave on “${DERIVE_FROM_DATE_LABEL}” for the normal case — the server applies the payment to the period the received date falls in.`}
                  </p>
                </div>
                {boardingPeriodBlocked && (
                  <p
                    className="text-small text-status-error border border-status-error rounded-md bg-status-error/10 px-space-3 py-space-2 m-0"
                    role="alert"
                    data-testid="period-boarding-blocked"
                  >
                    {BOARDING_PERIOD_MONTHLY_BLOCKED}
                  </p>
                )}
                {duplicateWarning && !boardingPeriodBlocked && (
                  <p
                    className="text-small text-status-warning border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2 m-0"
                    role="status"
                    data-testid="period-duplicate-warning"
                  >
                    {duplicateWarning}
                  </p>
                )}
                {oldestUnpaidBeforeSelection && (
                  <p
                    className="text-small text-status-warning border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2 m-0"
                    role="status"
                    data-testid="period-arrears-warning"
                  >
                    {arrearsWarning(formatCalendarDate(oldestUnpaidBeforeSelection.dueDate))}
                  </p>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-space-3">
                <div className="grid grid-cols-2 gap-space-3">
                  <div>
                    <Label htmlFor="entry-amount">Amount received</Label>
                    <Input
                      id="entry-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      autoFocus
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value);
                        setAmountTouched(true);
                        setPartialAck(false);
                        setPayAheadAck(false);
                      }}
                      // React SYNTHESISES onChange from its own value tracking, so
                      // an atomic replacement with a byte-identical string fires
                      // nothing at all: select-all and paste the figure already
                      // displayed — a staff member confirming it off a remittance
                      // advice — left the field looking entered while the re-derive
                      // above still counted it as the wizard's own, and a later
                      // ledger move then silently rewrote it. Marking the GESTURE
                      // closes that: pasting a figure asserts it, whether or not
                      // the resulting string differs. Typing needs no equivalent,
                      // since the first keystroke always changes the value.
                      //
                      // Gated on the payload carrying TEXT so a dragged file — or
                      // a pasted image, neither of which a number input accepts —
                      // cannot latch the flag and freeze the re-derive for the rest
                      // of the open. Chromium sanitises `$12,341.79` into `12341.79`
                      // rather than refusing it (verified in the real browser), so a
                      // differing figure changes the value and would set the flag
                      // through onChange regardless; this only has to catch the
                      // no-change case.
                      onPaste={(e) => {
                        if (e.clipboardData?.types?.includes("text/plain")) {
                          setAmountTouched(true);
                        }
                      }}
                      onDrop={(e) => {
                        if (e.dataTransfer?.types?.includes("text/plain")) {
                          setAmountTouched(true);
                        }
                      }}
                      data-testid="entry-amount"
                    />
                    {!isFeeEvent && expectedComparable != null && (
                      <p className="text-caption text-gray-mid m-0 mt-space-1">
                        Expected: {fmtMoney(expectedComparable)}
                        {/* Name both parts when the total is a sum — an
                            unexplained larger number is what staff would
                            override back down to the schedule figure. */}
                        {hasOpenFees && anchorPeriod && (
                          <>
                            {" — "}
                            <span data-testid="expected-fee-breakdown">
                              {expectedWithFeesBreakdown(
                                fmtMoney(
                                  anchorPeriod.expectedAmount - anchorPeriod.receivedAmount,
                                ),
                                fmtMoney(openFees),
                              )}
                            </span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="entry-date">Received date</Label>
                    <Input
                      id="entry-date"
                      type="date"
                      required
                      value={receivedDate}
                      onChange={(e) => setReceivedDate(e.target.value)}
                      data-testid="entry-date"
                    />
                  </div>
                </div>

                {dateIsFuture && (
                  <p
                    className="text-small text-status-error m-0"
                    role="alert"
                    data-testid="date-future-error"
                  >
                    {FUTURE_DATE_ERROR}
                  </p>
                )}
                {beforeFirstPayment && firstPaymentDay && (
                  <p className="text-small text-status-warning m-0" role="status" data-testid="date-warning">
                    {beforeFirstPaymentWarning(formatCalendarDate(loan.firstPaymentDate))}
                  </p>
                )}
                {staleDate && (
                  <p className="text-small text-status-warning m-0" role="status" data-testid="date-warning">
                    {STALE_DATE_WARNING}
                  </p>
                )}
                {derivedMismatch && derivedPeriod && selectedPeriod && (
                  <p className="text-small text-status-warning m-0" role="status" data-testid="date-warning">
                    {derivedPeriodMismatchWarning(
                      formatCalendarDate(derivedPeriod.dueDate),
                      formatCalendarDate(selectedPeriod.dueDate),
                    )}
                  </p>
                )}

                {/* Stated whenever fee money is outstanding, not only when the
                    entered amount is short — the point is that staff can see the
                    fee is IN the number before they decide whether to keep it. */}
                {hasOpenFees && !isFeeEvent && (
                  <p
                    className="text-small text-onyx border border-border-light rounded-md bg-white-warm px-space-3 py-space-2 m-0"
                    role="status"
                    data-testid="outstanding-fees-note"
                  >
                    {OUTSTANDING_FEES_EXPLANATION}
                  </p>
                )}

                {prefillMismatch && prefill && (
                  <p
                    className="text-small text-status-warning border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2 m-0"
                    role="status"
                    data-testid="entry-amount-mismatch"
                  >
                    {prefillMismatchWarning(
                      fmtMoney(prefill.expectedAmount),
                      fmtMoney(prefill.amount),
                    )}
                  </p>
                )}

                {belowExpected && (
                  <div
                    className="flex flex-col gap-space-2 border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2"
                    data-testid="partial-ack"
                  >
                    <p className="text-small text-onyx m-0">{PARTIAL_EXPLANATION}</p>
                    <label className="flex items-start gap-space-2 text-small text-onyx">
                      <input
                        type="checkbox"
                        checked={partialAck}
                        onChange={(e) => setPartialAck(e.target.checked)}
                        data-testid="partial-ack-checkbox"
                      />
                      {PARTIAL_ACK_LABEL}
                    </label>
                  </div>
                )}

                {aboveExpected && (
                  <div
                    className="flex flex-col gap-space-2 border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2"
                    data-testid="payahead-preview"
                  >
                    <p className="text-small text-onyx m-0">
                      {payAheadExplanation(payAheadSentence ?? "")}
                    </p>
                    {farAbove && (
                      <p
                        className="text-small text-status-error m-0"
                        role="alert"
                        data-testid="far-above-warning"
                      >
                        {FAR_ABOVE_WARNING}
                      </p>
                    )}
                    <label className="flex items-start gap-space-2 text-small text-onyx">
                      <input
                        type="checkbox"
                        checked={payAheadAck}
                        onChange={(e) => setPayAheadAck(e.target.checked)}
                        data-testid="payahead-ack-checkbox"
                      />
                      {PAY_AHEAD_ACK_LABEL}
                    </label>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="flex flex-col gap-space-3" data-testid="wizard-review">
                <dl className="grid grid-cols-[10rem_1fr] gap-y-space-2 text-small text-onyx m-0 p-space-3 border border-border-light rounded-md bg-white-warm">
                  <dt className="text-gray-mid">Loan</dt>
                  <dd className="m-0">{loan.loanNumber}</dd>
                  <dt className="text-gray-mid">Servicer (TPS)</dt>
                  <dd className="m-0">{tpsName}</dd>
                  <dt className="text-gray-mid">Payment type</dt>
                  <dd className="m-0">{selectedType.label}</dd>
                  <dt className="text-gray-mid">Applies to</dt>
                  <dd className="m-0">{periodLabel}</dd>
                  <dt className="text-gray-mid">Amount</dt>
                  <dd className="m-0">
                    {amountValid ? fmtMoney(parsedAmount) : "—"}
                    {/* The parenthetical is an assertion about what the staff
                        member DID, so it reads the acknowledgment itself. Keyed
                        on the mismatch instead, it claimed an attestation nobody
                        gave whenever the comparison moved after the amount step. */}
                    {belowExpected &&
                      (partialAck
                        ? " — partial payment (acknowledged)"
                        : " — partial payment")}
                    {aboveExpected &&
                      (payAheadAck ? " — pays ahead (acknowledged)" : " — pays ahead")}
                  </dd>
                  <dt className="text-gray-mid">Received date</dt>
                  <dd className="m-0">
                    {dateValid ? formatCalendarDate(`${receivedDate}T12:00:00.000Z`) : receivedDate}
                  </dd>
                </dl>

                {!amountStepValid && (
                  <p
                    className="text-small text-status-warning border border-status-warning rounded-md bg-status-warning/10 px-space-3 py-space-2 m-0"
                    role="alert"
                    data-testid="review-amount-recheck"
                  >
                    {REVIEW_AMOUNT_RECHECK_NOTICE}
                  </p>
                )}

                {prefill ? (
                  <div>
                    <Label>Payment reference</Label>
                    <p
                      className="text-small font-mono text-onyx m-0 border border-border-light rounded-md px-space-2 py-space-1 bg-white-warm break-all"
                      data-testid="entry-reference-locked"
                    >
                      {prefill.externalReference}
                    </p>
                    <p className="text-caption text-gray-mid m-0 mt-space-1">
                      {LOCKED_REFERENCE_NOTE}
                      {prefill.payerName ? ` — paid by ${prefill.payerName}` : ""}.
                    </p>
                  </div>
                ) : (
                  <p className="text-caption text-gray-mid m-0" data-testid="auto-reference-note">
                    {AUTO_REFERENCE_NOTE}
                  </p>
                )}

                <div>
                  <Label htmlFor="entry-notes">Notes</Label>
                  <Input
                    id="entry-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional"
                    data-testid="entry-notes"
                  />
                </div>

                {error && (
                  <p
                    className="text-small text-status-error m-0"
                    role="alert"
                    data-testid="entry-error"
                  >
                    {error}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {askAiOpen && (
          <RecordPaymentExplainModal
            loanId={loan.id}
            context={{
              paymentType,
              paymentTypeFacts: selectedType.facts,
              amount: amountValid ? parsedAmount : null,
              receivedDate: dateValid ? calendarDateInputToIso(receivedDate) : null,
              periodStart: selectedPeriod?.dueDate ?? prefill?.periodStart ?? null,
              stepKey: RECORD_PAYMENT_STEPS[step].key,
            }}
            onClose={() => setAskAiOpen(false)}
          />
        )}
      </div>
    </Modal>
  );
}
